import { Link } from 'react-router-dom';
import { siteInfo } from '@/mocks/site';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function About() {
  const pageMeta = {
    title: `关于我们 - ${siteInfo.name}`,
    description: `了解${siteInfo.name}的品牌故事、团队理念与联系方式。我们致力于为动漫爱好者提供最优质的正版同人志收藏指南。`,
    keywords: '关于我们,动漫本子收藏指南,品牌介绍,联系我们',
    canonicalUrl: `${siteInfo.url}/about`,
    ogType: 'website',
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'AboutPage',
        '@id': `${siteInfo.url}/about#about`,
        url: `${siteInfo.url}/about`,
        name: `关于我们 - ${siteInfo.name}`,
        description: `了解${siteInfo.name}的品牌故事、团队理念与联系方式。`,
        isPartOf: { '@id': `${siteInfo.url}/#website` },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.url },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: `${siteInfo.url}/about` },
        ],
      },
    ],
  };

  const brandValues = [
    {
      icon: 'ri-shield-check-line',
      title: '正版优先',
      description: '我们始终坚持推广正版同人志和授权商品，支持创作者的辛勤付出，维护健康的二次元创作生态。',
    },
    {
      icon: 'ri-heart-line',
      title: '热爱驱动',
      description: '团队由资深动漫爱好者和同人收藏家组成，每一篇文章都源于我们对二次元文化的真挚热爱。',
    },
    {
      icon: 'ri-guide-line',
      title: '专业可靠',
      description: '从选购攻略到保存技巧，每一篇内容都经过严格查证和实际经验验证，确保信息的准确性和实用性。',
    },
    {
      icon: 'ri-group-line',
      title: '社群共建',
      description: '我们相信最好的内容来自社群，鼓励读者分享经验、提出建议，共同打造最全面的同人收藏知识库。',
    },
  ];

  const milestones = [
    { year: '2018', event: '网站正式上线，从个人博客起步' },
    { year: '2019', event: '首次参展ComicUp，现场报道同人展盛况' },
    { year: '2020', event: '建立同人本数据库，收录超500本正版同人志信息' },
    { year: '2021', event: '推出收藏技巧专栏，服务超过10万动漫爱好者' },
    { year: '2022', event: '首次赴日报道Comiket，拓展海外同人文化视野' },
    { year: '2023', event: '读者社群突破5万人，成为国内最大同人收藏社区之一' },
    { year: '2024', event: '推出展会攻略系列文章，覆盖国内外主要同人展会' },
    { year: '2025', event: '内容全面升级，新增周边收藏和投资指南栏目' },
    { year: '2026', event: '持续优化内容质量，打造最专业的动漫本子收藏指南平台' },
  ];

  return (
    <>
      <SEOHead meta={pageMeta} />
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4 text-center">
              关于我们
            </h1>
            <p className="text-base text-foreground-600 text-center max-w-2xl mx-auto leading-relaxed mb-12">
              {siteInfo.name}创立于{siteInfo.foundingYear}年，
              是专注于正版同人志、角色设定集、漫画手册和动漫周边收藏的专业内容平台。
              我们致力于为每一位动漫爱好者提供最全面、最实用的收藏指南。
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-14">
              {brandValues.map((value) => (
                <div key={value.title} className="bg-background-100 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-lg shrink-0">
                      <i className={`${value.icon} text-xl text-primary-600`}></i>
                    </div>
                    <div>
                      <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                        {value.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{value.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="relative mb-14">
              <div className="absolute left-4 md:left-6 top-0 bottom-0 w-px bg-primary-200"></div>
              <div className="space-y-8">
                {milestones.map((m) => (
                  <div key={m.year} className="flex items-start gap-6 md:gap-5 relative">
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-primary-500 flex items-center justify-center shrink-0 relative z-10">
                      <span className="text-xs md:text-sm font-bold text-white">{m.year.slice(2)}</span>
                    </div>
                    <div className="flex-1 min-w-0 pt-1 md:pt-2">
                      <span className="text-sm font-semibold text-foreground-900">{m.year}</span>
                      <p className="text-sm text-foreground-600 mt-1">{m.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3 text-center">
              用户口碑
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-10">来自收藏爱好者的真实评价</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="bg-background-50 rounded-lg p-6">
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-sm text-accent-500"></i>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  "作为一个刚入坑的同人志收藏新手，这个网站的攻略对我帮助太大了！从怎么辨别正版到如何保存，每篇文章都写得特别详细。现在已经收藏了20多本了。"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center">
                    <span className="text-xs font-semibold text-secondary-700">李</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">李同学</p>
                    <p className="text-xs text-foreground-500">大学生 · 收藏不到1年</p>
                  </div>
                </div>
              </div>

              <div className="bg-background-50 rounded-lg p-6">
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-sm text-accent-500"></i>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  "展会攻略真的救了我！CP30那次照着攻略准备的，抢到了好多热门的同人本。现在每次参展前都会来看看有没有新的攻略更新。"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center">
                    <span className="text-xs font-semibold text-secondary-700">王</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">王小二</p>
                    <p className="text-xs text-foreground-500">社畜 · 收藏3年</p>
                  </div>
                </div>
              </div>

              <div className="bg-background-50 rounded-lg p-6">
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-sm text-accent-500"></i>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  "从2019年开始关注这个网站，眼看着内容越来越丰富。设定集评测对标日本的收藏杂志毫不逊色，每次新品发售前都会先来看看值不值得入手。"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center">
                    <span className="text-xs font-semibold text-secondary-700">陈</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">陈老师</p>
                    <p className="text-xs text-foreground-500">资深收藏家 · 收藏7年</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3 text-center">
              联系我们
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-10">
              有任何问题或合作意向，欢迎通过以下方式联系我们
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
              <div className="bg-background-100 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center bg-primary-100 rounded-full mb-4">
                  <i className="ri-mail-line text-xl text-primary-600"></i>
                </div>
                <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1.5">电子邮箱</h3>
                <a href={`mailto:${siteInfo.email}`} className="text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer">
                  {siteInfo.email}
                </a>
              </div>

              <div className="bg-background-100 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center bg-primary-100 rounded-full mb-4">
                  <i className="ri-phone-line text-xl text-primary-600"></i>
                </div>
                <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1.5">联系电话</h3>
                <a href={`tel:${siteInfo.phone}`} className="text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer">
                  {siteInfo.phone}
                </a>
              </div>

              <div className="bg-background-100 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center bg-primary-100 rounded-full mb-4">
                  <i className="ri-map-pin-line text-xl text-primary-600"></i>
                </div>
                <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1.5">公司地址</h3>
                <p className="text-sm text-foreground-600">{siteInfo.address}</p>
              </div>
            </div>

            <div className="bg-background-100 rounded-lg p-6 md:p-8">
              <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4 text-center">关注我们的社交平台</h3>
              <div className="flex items-center justify-center gap-6">
                <a
                  href={siteInfo.socialLinks.weibo}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="flex flex-col items-center gap-2 text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-secondary-100 rounded-full">
                    <i className="ri-weibo-line text-lg"></i>
                  </div>
                  <span className="text-xs">微博</span>
                </a>
                <a
                  href={siteInfo.socialLinks.bilibili}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="flex flex-col items-center gap-2 text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-secondary-100 rounded-full">
                    <i className="ri-bilibili-line text-lg"></i>
                  </div>
                  <span className="text-xs">Bilibili</span>
                </a>
                <a
                  href={siteInfo.socialLinks.xiaohongshu}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="flex flex-col items-center gap-2 text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-secondary-100 rounded-full">
                    <i className="ri-chat-smile-2-line text-lg"></i>
                  </div>
                  <span className="text-xs">小红书</span>
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-2xl mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              加入我们的收藏社群
            </h2>
            <p className="text-sm text-foreground-600 mb-8">
              获取最新同人本资讯、展会攻略和独家收藏福利
            </p>
            <Link
              to="/category"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-white rounded-full font-medium text-sm hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <span>开始探索</span>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </div>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}